import { useEffect, useState } from "react";

import {
  type KeystoneOfferEligibility,
  type OfferStatusCode,
} from "data/keystone-api-mapping";
import { useKeystone, usePolicyDetail } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { logException, trackEvent, useFlags } from "utils";

const ENROLLED_STATUSES: OfferStatusCode[] = ["ENROLLED", "ORDERED"];

export const useEligibleOffers = (policyId?: string) => {
  const keystone = useKeystone();
  const { shingleResealOffer, tingOffer } = useFlags();
  const [rawOffers, setRawOffers] = useState<KeystoneOfferEligibility[]>([]);
  const [allOffers, setAllOffers] = useState<KeystoneOfferEligibility[]>([]);
  const [enrolledOffers, setEnrolledOffers] = useState<
    KeystoneOfferEligibility[]
  >([]);
  const [unenrolledOffers, setUnenrolledOffers] = useState<
    KeystoneOfferEligibility[]
  >([]);
  const [loading, setLoading] = useState(true);
  const { data, isLoading: enrollmentLoading } = usePolicyDetail(policyId);

  // Go get the offers
  useEffect(() => {
    setRawOffers([]);
    if (!policyId) {
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const res = await keystone.getPolicyOffers(policyId);
        if (!isError(res)) {
          const offers = res;
          setRawOffers(offers);
          trackEvent("Eligible Offers Loaded", {
            offers: offers.map((offer) => offer.offeringInternalName),
            keystonePolicyId: policyId,
          });
        }
      } catch (err) {
        // swallow the error, we just won't show any offers
        logException(err);
      }
      setLoading(false);
    })();
  }, [keystone, policyId]);

  // Filter the eligible offers
  useEffect(() => {
    const implementedOfferingNames: string[] = ["leakbot"];
    if (tingOffer) {
      implementedOfferingNames.push("ting-offer");
    }
    if (shingleResealOffer) {
      implementedOfferingNames.push("wind-defense");
    }

    const offers = [...rawOffers];
    if (policyId && data?.userDetails.isEnrolledInLeakBot) {
      offers.push({
        isFeatured: false,
        insightPolicyId: policyId,
        isEligible: true,
        offerId: null,
        offerStatusCode: "ENROLLED",
        offerUrl: "",
        offeringId: "",
        offeringInternalName: "leakbot",
        offeringMarketingName: "LeakBot",
        additionalDetails: [],
      });
    }

    setAllOffers(
      offers.filter(
        (offer) =>
          offer.isEligible &&
          implementedOfferingNames.includes(offer.offeringInternalName),
      ),
    );
    setEnrolledOffers(
      offers.filter(
        (offer) =>
          offer?.offerStatusCode &&
          ENROLLED_STATUSES.includes(offer?.offerStatusCode) &&
          implementedOfferingNames.includes(offer.offeringInternalName),
      ),
    );
    setUnenrolledOffers(
      offers.filter(
        (offer) =>
          offer.isEligible &&
          (offer?.offerStatusCode === null ||
            !ENROLLED_STATUSES.includes(offer?.offerStatusCode)) &&
          implementedOfferingNames.includes(offer.offeringInternalName),
      ),
    );
  }, [
    data?.userDetails.isEnrolledInLeakBot,
    policyId,
    rawOffers,
    shingleResealOffer,
    tingOffer,
  ]);

  return {
    eligibleOffers: {
      allOffers,
      enrolledOffers,
      unenrolledOffers,
    },
    loading: !policyId || loading || enrollmentLoading,
  };
};
